import React, { useState } from 'react';
import { usePedirTexto } from './ConfirmDialog';
import { Icon } from './Icon';
import { TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../../utils/statusColors';

/**
 * "COPIAR LINK" DE UM SIGNATÁRIO (15/10/2026: extraído do DocumentViewerModal para servir também ao "Status das
 * Assinaturas" do contrato digital no CRM, pedido do Heitor).
 *
 * O link é o DESTE signatário, montado da chave dele (`clicksignSignerKey`). Sem a chave própria, não há botão: nunca o
 * link de outro signatário, nunca o `signatureUrl` do documento (que é o do PRIMEIRO, e para os outros é o link errado).
 * Só para quem não assinou. Com o clipboard bloqueado, mostra o link para copiar à mão.
 *
 * Existe porque o e-mail do ClickSign não chega sempre e não há como depurar isso do nosso lado (OF-2026/059, 28/09/2026:
 * tudo certo no ClickSign, e o e-mail não chegou). O link não dispensa a autenticação do signatário: leva à tela de
 * assinatura, não assina por ninguém.
 */
export interface SignatarioComChave {
  name: string;
  clicksignSignerKey?: string | null;
}

export function linkDeAssinatura(signer: SignatarioComChave): string | null {
  return signer.clicksignSignerKey ? `https://app.clicksign.com/sign/${signer.clicksignSignerKey}` : null;
}

export default function CopiarLinkDeAssinatura({ signer, className = '' }: { signer: SignatarioComChave; className?: string }) {
  const pedirTexto = usePedirTexto();
  const [copiado, setCopiado] = useState(false);
  const link = linkDeAssinatura(signer);
  if (!link) return null;

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Clipboard bloqueado (http, permissão negada): mostra o link para copiar na mão, em vez de falhar calado.
      await pedirTexto({
        title: 'Copie o link de assinatura',
        message: 'O navegador não deixou copiar sozinho. Selecione o link e copie.',
        label: 'Link',
        valorInicial: link,
        somenteLeitura: true,
        confirmLabel: 'Pronto',
        cancelLabel: 'Fechar',
      });
    }
  };

  return (
    <button
      type='button'
      onClick={copiar}
      title={'Copiar o link de assinatura de ' + signer.name + '. Use quando o e-mail do ClickSign não chegar.'}
      aria-label={'Copiar o link de assinatura de ' + signer.name}
      className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded-md border transition-colors ${
        copiado ? `${TONE_BORDER.open} ${TONE_SURFACE.open} ${TONE_TEXT.open}` : 'border-slate-600 text-slate-300 hover:bg-slate-700/60 hover:text-white'
      } ${className}`}
    >
      <Icon name={copiado ? 'check' : 'copy'} className='w-3.5 h-3.5' />
      {copiado ? 'Link copiado' : 'Copiar link'}
    </button>
  );
}
